import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {useFormik} from 'formik'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import DOMPurify from 'dompurify'
import {
  fillWithExamples,
  findUnknownParams,
  IMailTemplateFormValues,
  IMailTemplateModel,
  IMailTemplateParam,
  initialMailTemplateForm,
  toMailTemplateFormValues,
  toMailTemplateModel,
  validationMailTemplate,
} from './MailTemplateModel'

const EDITOR_MODULES = {
  toolbar: [
    [{header: [2, 3, false]}],
    ['bold', 'italic', 'underline'],
    [{list: 'ordered'}, {list: 'bullet'}],
    [{align: []}],
    ['link'],
    ['clean'],
  ],
}

type Props = {
  show: boolean
  editingItem: IMailTemplateModel | null
  params: IMailTemplateParam[]
  paramsLoading: boolean
  loading: boolean
  onClose: () => void
  onSubmit: (template: IMailTemplateModel) => void
}

/** Parametre eklenecek alan; kullanıcının son dokunduğu alan hedef olur */
type ParamTarget = 'subject' | 'body'

export const MailTemplateFormModal: FC<Props> = ({
  show,
  editingItem,
  params,
  paramsLoading,
  loading,
  onClose,
  onSubmit,
}) => {
  const isEditing = !!editingItem
  const [paramTarget, setParamTarget] = useState<ParamTarget>('body')
  const [showPreview, setShowPreview] = useState(false)
  const subjectRef = useRef<HTMLInputElement | null>(null)
  const quillRef = useRef<ReactQuill | null>(null)

  const formik = useFormik<IMailTemplateFormValues>({
    initialValues: initialMailTemplateForm,
    validationSchema: validationMailTemplate,
    onSubmit: (values) => onSubmit(toMailTemplateModel(values)),
  })

  // Modal her açıldığında formu yeni kayıt / düzenlenen kayıt ile doldur
  useEffect(() => {
    if (!show) return
    formik.resetForm({
      values: editingItem ? toMailTemplateFormValues(editingItem) : initialMailTemplateForm,
    })
    setParamTarget('body')
    setShowPreview(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editingItem])

  const fieldError = (field: keyof IMailTemplateFormValues) =>
    formik.touched[field] && formik.errors[field] ? (formik.errors[field] as string) : undefined

  /** Parametreyi son dokunulan alanın imleç konumuna ekler */
  const insertParam = (token: string) => {
    if (paramTarget === 'subject') {
      const subject = formik.values.subject
      const input = subjectRef.current
      // Butona tıklanınca alan odağı kaybeder ama imleç konumu korunur
      const at = input?.selectionStart ?? subject.length
      formik.setFieldValue('subject', `${subject.slice(0, at)}${token}${subject.slice(at)}`)
      if (input) {
        // Değer formik üzerinden güncellendiği için imleci bir sonraki tick'te taşı
        window.setTimeout(() => {
          input.focus()
          input.setSelectionRange(at + token.length, at + token.length)
        }, 0)
      }
      return
    }

    const editor = quillRef.current?.getEditor()
    if (!editor) return
    const range = editor.getSelection(true)
    const at = range ? range.index : editor.getLength()
    editor.insertText(at, token, 'user')
    editor.setSelection(at + token.length, 0)
  }

  const unknownParams = useMemo(
    () =>
      Array.from(
        new Set([
          ...findUnknownParams(formik.values.subject, params),
          ...findUnknownParams(formik.values.body, params),
        ])
      ),
    [formik.values.subject, formik.values.body, params]
  )

  const previewSubject = useMemo(
    () => fillWithExamples(formik.values.subject, params),
    [formik.values.subject, params]
  )

  const previewBody = useMemo(
    () => DOMPurify.sanitize(fillWithExamples(formik.values.body, params)),
    [formik.values.body, params]
  )

  return (
    <Modal
      show={show}
      onHide={onClose}
      size='xl'
      dialogClassName='modal-dialog-centered'
      backdrop='static'
      enforceFocus={false}
    >
      <form onSubmit={formik.handleSubmit} noValidate>
        <div className='modal-header'>
          <h5 className='modal-title'>{isEditing ? 'Mail Şablonu Düzenle' : 'Yeni Mail Şablonu'}</h5>
          <button type='button' className='btn-close' onClick={onClose} />
        </div>

        <div className='modal-body'>
          <div className='row g-5'>
            <div className='col-lg-8'>
              <div className='row g-5'>
                <div className='col-md-7'>
                  <label className='form-label fw-bold required'>Şablon Adı</label>
                  <input
                    type='text'
                    autoFocus
                    className={`form-control ${fieldError('name') ? 'is-invalid' : ''}`}
                    placeholder='Örn: Mülakat daveti'
                    {...formik.getFieldProps('name')}
                  />
                  {fieldError('name') && <div className='invalid-feedback'>{fieldError('name')}</div>}
                </div>

                <div className='col-md-5'>
                  <label className='form-label fw-bold'>Açıklama</label>
                  <input
                    type='text'
                    className={`form-control ${fieldError('description') ? 'is-invalid' : ''}`}
                    placeholder='Şablonun nerede kullanıldığı'
                    {...formik.getFieldProps('description')}
                  />
                  {fieldError('description') && (
                    <div className='invalid-feedback'>{fieldError('description')}</div>
                  )}
                </div>

                <div className='col-12'>
                  <label className='form-label fw-bold required'>Mail Konusu</label>
                  <input
                    type='text'
                    className={`form-control ${fieldError('subject') ? 'is-invalid' : ''}`}
                    placeholder='Örn: {{jobTitle}} başvurunuz hakkında'
                    {...formik.getFieldProps('subject')}
                    ref={subjectRef}
                    onFocus={() => setParamTarget('subject')}
                  />
                  {fieldError('subject') ? (
                    <div className='invalid-feedback'>{fieldError('subject')}</div>
                  ) : (
                    <div className='form-text'>
                      Parametre eklemek için alana tıklayın, ardından sağdaki listeden seçin.
                    </div>
                  )}
                </div>

                <div className='col-12'>
                  <label className='form-label fw-bold required'>Mail İçeriği</label>
                  <div onFocus={() => setParamTarget('body')}>
                    <ReactQuill
                      ref={quillRef}
                      theme='snow'
                      modules={EDITOR_MODULES}
                      value={formik.values.body}
                      onChange={(html) => formik.setFieldValue('body', html)}
                      onBlur={() => formik.setFieldTouched('body', true)}
                      placeholder='Merhaba {{candidateFirstname}}, ...'
                    />
                  </div>
                  {fieldError('body') && (
                    <div className='text-danger fs-7 mt-1'>{fieldError('body')}</div>
                  )}
                </div>

                {unknownParams.length > 0 && (
                  <div className='col-12'>
                    <div className='alert alert-warning mb-0 py-3'>
                      Tanımsız parametre: {unknownParams.map((param) => `{{${param}}}`).join(', ')} —
                      kaydetmek için listedeki parametreleri kullanınız.
                    </div>
                  </div>
                )}

                <div className='col-12 d-flex flex-wrap gap-5 align-items-center'>
                  <div className='form-check form-switch form-check-custom form-check-solid'>
                    <input
                      id='mail-template-active'
                      type='checkbox'
                      className='form-check-input'
                      checked={formik.values.active}
                      onChange={(event) => formik.setFieldValue('active', event.target.checked)}
                    />
                    <label className='form-check-label fw-bold' htmlFor='mail-template-active'>
                      Aktif
                    </label>
                  </div>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => setShowPreview((previous) => !previous)}
                  >
                    <i className='bi bi-eye'></i>
                    {showPreview ? 'Önizlemeyi Kapat' : 'Önizleme'}
                  </button>
                </div>

                {showPreview && (
                  <div className='col-12'>
                    <div className='border border-gray-300 rounded p-5 bg-light-primary'>
                      <div className='text-muted fs-7'>Örnek değerlerle önizleme</div>
                      <div className='fw-bolder fs-5 mb-3'>{previewSubject || 'Konu yazılmadı'}</div>
                      <div
                        className='bg-white rounded p-4'
                        dangerouslySetInnerHTML={{__html: previewBody}}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='col-lg-4'>
              <label className='form-label fw-bold'>Parametreler</label>
              <div className='text-muted fs-8 mb-3'>
                Mail gönderilirken adayın bilgileriyle değiştirilir. Eklenecek alan:{' '}
                <span className='fw-bolder'>{paramTarget === 'subject' ? 'Konu' : 'İçerik'}</span>
              </div>
              {paramsLoading ? (
                <div className='d-flex justify-content-center py-5'>
                  <span className='spinner-border spinner-border-sm text-primary' />
                </div>
              ) : (
                <div
                  className='d-flex flex-column gap-2 overflow-auto border border-gray-300 rounded p-3'
                  style={{maxHeight: 420}}
                >
                  {params.map((param) => (
                    <button
                      key={param.key}
                      type='button'
                      className='btn btn-sm btn-light text-start py-2'
                      title={`${param.label} (örn. ${param.example})`}
                      onClick={() => insertParam(param.token)}
                    >
                      <span className='fw-bolder d-block text-primary'>{param.token}</span>
                      <span className='text-muted fs-8'>{param.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='modal-footer'>
          <button type='button' className='btn btn-light' onClick={onClose}>
            İptal
          </button>
          <button type='submit' className='btn btn-primary' disabled={loading}>
            {loading ? (
              <span className='indicator-progress' style={{display: 'block'}}>
                Lütfen bekleyin...
                <span className='spinner-border spinner-border-sm align-middle ms-2' />
              </span>
            ) : isEditing ? (
              'Güncelle'
            ) : (
              'Kaydet'
            )}
          </button>
        </div>
      </form>
    </Modal>
  )
}
